import React, { useMemo, useState } from "react";
import { Box } from "@mui/system";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import InputField from "../../../../../components/InputFields/InputField";
import Checkbox from "../../../../../components/Buttons/Checkbox/Checkbox";
import { IconComponent } from "../../../../../components/Icon";
import {
  ACTIONS,
  BASE,
  BORDER,
  GREY,
  PRIMARY,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import { STRINGS } from "../../strings";

// Groups the API is expected to use, in the order they should appear. Anything
// else it sends still renders, appended in first-seen order under its own tab.
const KNOWN_GROUP_ORDER = ["people", "company", "custom"];
const GROUP_LABELS = {
  people: STRINGS.CUSTOMIZATION_KEYS_PEOPLE,
  company: STRINGS.CUSTOMIZATION_KEYS_COMPANY,
  custom: STRINGS.CUSTOMIZATION_KEYS_CUSTOM,
};
// `group` is optional in the contract, so a response that omits it entirely
// collapses to one flat list rather than a single pointless tab.
const UNGROUPED = "__ungrouped__";

const KeyRow = ({ item, checked, onToggle, isLast }) => (
  <Box
    sx={{
      ...stepStyles.row,
      borderBottom: isLast ? "none" : `1px solid ${BORDER.LIGHT}`,
    }}
    onClick={() => onToggle(item.key)}
  >
    {/* The whole row is clickable, so the checkbox must swallow its own click
        or the row handler would fire straight after and undo the toggle. */}
    <Box sx={stepStyles.rowCheckbox} onClick={(e) => e.stopPropagation()}>
      <Checkbox
        noMargin
        checked={checked}
        onChange={() => onToggle(item.key)}
        aria-label={item.label}
      />
    </Box>
    <Box sx={stepStyles.rowTag}>
      <TypographyInline
        size={"sm"}
        color={checked ? PRIMARY.W_500 : GREY.SECONDARY}
        fontWeight={fontWeight.MEDIUM}
        label={`{{${item.key}}}`}
        isEllipses
      />
    </Box>
    <Box sx={stepStyles.rowLabel} title={item.label || ""}>
      <TypographyInline
        size={"sm"}
        color={GREY.PRIMARY}
        fontWeight={fontWeight.REGULAR}
        label={item.label}
        isEllipses
      />
    </Box>
  </Box>
);

const CenteredMessage = ({ icon, message, action, onAction }) => (
  <Box sx={stepStyles.centered}>
    {icon ? (
      <IconComponent iconLabel={icon} color={GREY.DISABLED} fontSize={"20px"} />
    ) : null}
    <TypographyInline
      size={"sm"}
      color={GREY.TERTIARY}
      fontWeight={fontWeight.REGULAR}
      label={message}
    />
    {action ? (
      <Box sx={stepStyles.textAction} onClick={onAction}>
        <TypographyInline
          size={"sm"}
          color={PRIMARY.W_500}
          fontWeight={fontWeight.MEDIUM}
          label={action}
        />
      </Box>
    ) : null}
  </Box>
);

const CustomizationKeysStep = ({
  classes,
  currentStep,
  totalSteps,
  // Keys the selected contact list exposes: {key, label, sample, group}.
  listKeys = [],
  selectedKeys = [],
  onToggleKey,
  onSelectAllKeys,
  onClearKeys,
  loading = false,
  errorMessage = "",
  onRetry,
  hasList = true,
  contactCount = null,
}) => {
  const [tab, setTab] = useState("");
  const [search, setSearch] = useState("");

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  // Bucket by group, keeping the known groups in a fixed order and appending
  // anything unexpected after them.
  const buckets = useMemo(() => {
    const map = new Map();
    (listKeys || []).forEach((item) => {
      const group = item?.group || UNGROUPED;
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(item);
    });
    const ordered = [];
    KNOWN_GROUP_ORDER.forEach((group) => {
      if (map.has(group)) ordered.push({ id: group, items: map.get(group) });
    });
    map.forEach((items, group) => {
      if (!KNOWN_GROUP_ORDER.includes(group) && group !== UNGROUPED) {
        ordered.push({ id: group, items });
      }
    });
    if (map.has(UNGROUPED)) {
      ordered.push({ id: UNGROUPED, items: map.get(UNGROUPED) });
    }
    return ordered;
  }, [listKeys]);

  // One bucket means the group carries no information, so the tab strip is
  // dropped and every key renders in a single list.
  const showTabs = buckets.length > 1;
  const activeTab =
    showTabs && buckets.some((bucket) => bucket.id === tab)
      ? tab
      : buckets[0]?.id || "";

  const visibleKeys = useMemo(() => {
    const source = showTabs
      ? buckets.find((bucket) => bucket.id === activeTab)?.items || []
      : listKeys || [];
    const query = search?.toLowerCase()?.trim();
    if (!query) return source;
    return source.filter(
      (item) =>
        item?.label?.toLowerCase()?.includes(query) ||
        item?.key?.toLowerCase()?.includes(query),
    );
  }, [buckets, showTabs, activeTab, listKeys, search]);

  const selectedCount = selectedKeys?.length || 0;
  const totalCount = listKeys?.length || 0;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  const renderBody = () => {
    if (!hasList) {
      return (
        <CenteredMessage
          icon={ICON_LABELS.DATA_OBJECT}
          message={STRINGS.CUSTOMIZATION_KEYS_NO_LIST}
        />
      );
    }
    if (loading) {
      return <CenteredMessage message={STRINGS.CUSTOMIZATION_KEYS_LOADING} />;
    }
    if (errorMessage) {
      return (
        <CenteredMessage
          icon={ICON_LABELS.INFO}
          message={errorMessage}
          action={onRetry ? STRINGS.CUSTOMIZATION_KEYS_RETRY : ""}
          onAction={onRetry}
        />
      );
    }
    if (!totalCount) {
      return (
        <CenteredMessage
          icon={ICON_LABELS.DATA_OBJECT}
          message={STRINGS.CUSTOMIZATION_KEYS_EMPTY}
        />
      );
    }

    return (
      <>
        {showTabs ? (
          <Box sx={stepStyles.tabStrip}>
            {buckets.map((bucket) => {
              const isActive = activeTab === bucket.id;
              const bucketSelected = bucket.items.filter((item) =>
                selectedSet.has(item.key),
              ).length;
              const label =
                GROUP_LABELS[bucket.id] ||
                (bucket.id === UNGROUPED
                  ? STRINGS.CUSTOMIZATION_KEYS_OTHER
                  : bucket.id);
              return (
                <Box
                  key={bucket.id}
                  sx={{
                    ...stepStyles.tab,
                    borderBottom: isActive
                      ? `2px solid ${PRIMARY.W_500}`
                      : "2px solid transparent",
                  }}
                  onClick={() => {
                    if (bucket.id !== activeTab) setSearch("");
                    setTab(bucket.id);
                  }}
                >
                  <TypographyInline
                    size={"sm"}
                    color={isActive ? PRIMARY.W_500 : GREY.SECONDARY}
                    fontWeight={
                      isActive ? fontWeight.SEMIBOLD : fontWeight.MEDIUM
                    }
                    label={`${label} (${bucketSelected}/${bucket.items.length})`}
                  />
                </Box>
              );
            })}
          </Box>
        ) : null}

        <Box sx={stepStyles.toolbar}>
          <Box sx={stepStyles.searchBox}>
            <InputField
              size={"sm36"}
              placeholder={STRINGS.CUSTOMIZATION_KEYS_SEARCH}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box sx={stepStyles.toolbarRight}>
            <TypographyInline
              size={"xs"}
              color={GREY.SECONDARY}
              fontWeight={fontWeight.MEDIUM}
              label={`${selectedCount} of ${totalCount} ${STRINGS.CUSTOMIZATION_KEYS_SELECTED}`}
            />
            <Box
              sx={stepStyles.textAction}
              onClick={allSelected ? undefined : onSelectAllKeys}
            >
              <TypographyInline
                size={"xs"}
                color={allSelected ? GREY.DISABLED : PRIMARY.W_500}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.CUSTOMIZATION_KEYS_SELECT_ALL}
              />
            </Box>
            <Box
              sx={stepStyles.textAction}
              onClick={selectedCount ? onClearKeys : undefined}
            >
              <TypographyInline
                size={"xs"}
                color={selectedCount ? PRIMARY.W_500 : GREY.DISABLED}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.CUSTOMIZATION_KEYS_CLEAR}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={stepStyles.listShell}>
          <Box sx={stepStyles.listHeader}>
            <Box sx={stepStyles.rowCheckbox} />
            <Box sx={stepStyles.rowTag}>
              <TypographyInline
                size={"xs"}
                color={GREY.TERTIARY}
                fontWeight={fontWeight.MEDIUM}
                label={"Key"}
              />
            </Box>
            <Box sx={stepStyles.rowLabel}>
              <TypographyInline
                size={"xs"}
                color={GREY.TERTIARY}
                fontWeight={fontWeight.MEDIUM}
                label={"Field"}
              />
            </Box>
          </Box>

          <Box sx={stepStyles.listBody}>
            {visibleKeys.length ? (
              visibleKeys.map((item, index) => (
                <KeyRow
                  key={item.key}
                  item={item}
                  checked={selectedSet.has(item.key)}
                  onToggle={onToggleKey}
                  isLast={index === visibleKeys.length - 1}
                />
              ))
            ) : (
              <CenteredMessage
                icon={ICON_LABELS.DATA_OBJECT}
                message={STRINGS.CUSTOMIZATION_KEYS_NO_MATCH}
              />
            )}
          </Box>
        </Box>
      </>
    );
  };

  return (
    <Box data-tour="campaign-customization-keys-section">
      <div className={classes.div}>
        <TypographyInline
          size={"md"}
          color={GREY.PRIMARY}
          fontWeight={fontWeight.SEMIBOLD}
          label={STRINGS.CUSTOMIZATION_KEYS}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={currentStep ? `Step ${currentStep} of ${totalSteps}` : ""}
        />
      </div>

      <Box className={classes.containStep}>
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={STRINGS.CUSTOMIZATION_KEYS_DESC}
        />
        {/* total_contacts off the same response: says how much of the list the
            keys were read from. Only meaningful once keys actually loaded. */}
        {!loading && !errorMessage && totalCount && contactCount !== null ? (
          <Box sx={{ marginTop: "4px" }}>
            <TypographyInline
              size={"xs"}
              color={GREY.TERTIARY}
              fontWeight={fontWeight.REGULAR}
              label={`Found across ${contactCount} ${
                contactCount === 1
                  ? STRINGS.CONTACT.toLowerCase()
                  : STRINGS.CONTACTS.toLowerCase()
              } in this list`}
            />
          </Box>
        ) : null}
        {renderBody()}
      </Box>
    </Box>
  );
};

const stepStyles = {
  tabStrip: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    marginTop: "14px",
    borderBottom: `1px solid ${BORDER.LIGHT}`,
    overflowX: "auto",
  },
  tab: {
    paddingBottom: "6px",
    // Sits the active underline on top of the strip's own hairline instead of
    // one pixel below it.
    marginBottom: "-1px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
    marginTop: "12px",
  },
  searchBox: { width: "260px", maxWidth: "100%" },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  textAction: { cursor: "pointer", whiteSpace: "nowrap" },
  listShell: {
    marginTop: "10px",
    border: `1px solid ${BORDER.DEFAULT}`,
    borderRadius: "8px",
    background: BASE.WHITE,
    overflow: "hidden",
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 12px",
    background: ACTIONS.HOVER,
    borderBottom: `1px solid ${BORDER.LIGHT}`,
  },
  listBody: {
    maxHeight: "280px",
    overflowY: "auto",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px 12px",
    cursor: "pointer",
    "&:hover": { background: ACTIONS.HOVER },
  },
  rowCheckbox: { width: "20px", flexShrink: 0, display: "flex" },
  rowTag: { width: "45%", minWidth: 0 },
  rowLabel: { flex: 1, minWidth: 0 },
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "48px 16px",
    textAlign: "center",
  },
};

export default React.memo(CustomizationKeysStep);
